import { useState, useEffect, useMemo } from "react";
import { DateField } from "@/components/ui/date-field";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { PenLine, Sparkles, FileText, Send, ArrowLeft, ArrowRight, User, Building2, Briefcase, Table2, CheckCircle2, Download } from "lucide-react";
import IncassoLetterFlow from "@/components/IncassoLetterFlow";

const SETS = [
  { v: "herinnering_1", l: "1e Herinnering" },
  { v: "herinnering_2", l: "2e Herinnering" },
  { v: "aanmaning", l: "Aanmaning" },
  { v: "algemeen", l: "Algemeen" },
];
const TONES = ["Vriendelijk", "Dringend", "Formeel Juridisch"];

export default function BrievenGenerator() {
  const [sp] = useSearchParams();
  const [step, setStep] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [dossiers, setDossiers] = useState([]);

  const [recipientType, setRecipientType] = useState("debiteur");
  const [tenantId, setTenantId] = useState("");
  const [contactSource, setContactSource] = useState("opdrachtgever"); // opdrachtgever | pand
  const [clientId, setClientId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactFunction, setContactFunction] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [setType, setSetType] = useState("herinnering_1");
  const [tone, setTone] = useState("Vriendelijk");
  const [mode, setMode] = useState("ai");
  const [useGeneral, setUseGeneral] = useState(false);
  const [templateClientId, setTemplateClientId] = useState("general");
  const [instruction, setInstruction] = useState("");
  const [months, setMonths] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [specification, setSpecification] = useState("");

  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [specRows, setSpecRows] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [dossierId, setDossierId] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [defaultTo, setDefaultTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedLetterId, setSavedLetterId] = useState("");
  const [flow, setFlow] = useState("standaard");

  useEffect(() => {
    Promise.all([api.get("/tenants"), api.get("/clients"), api.get("/properties")])
      .then(([t, c, p]) => { setTenants(t.data); setClients(c.data); setProperties(p.data); })
      .catch(() => {});
  }, []);

  // prefill from query params
  useEffect(() => {
    const qt = sp.get("tenant"); const qc = sp.get("client");
    if (sp.get("incasso") === "1") setFlow("incasso");
    if (qt) { setRecipientType("debiteur"); setTenantId(qt); }
    else if (qc) { setRecipientType("contact"); setContactSource("opdrachtgever"); setClientId(qc); }
  }, [sp]);

  useEffect(() => {
    if (recipientType === "debiteur" && tenantId) {
      api.get(`/tenants/${tenantId}/dossiers`).then((r) => setDossiers(r.data)).catch(() => setDossiers([]));
    }
  }, [recipientType, tenantId]);

  const selectedClient = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);
  const selectedProperty = useMemo(() => properties.find((p) => p.id === propertyId), [properties, propertyId]);
  const contactOptions = useMemo(() => {
    const src = contactSource === "pand" ? selectedProperty : selectedClient;
    const arr = [];
    if (contactSource === "opdrachtgever" && selectedClient) {
      if (selectedClient.contact_person) arr.push({ name: selectedClient.contact_person, function: selectedClient.contact_function || "Primair contact", email: selectedClient.email });
      if (selectedClient.contact_person2) arr.push({ name: selectedClient.contact_person2, function: selectedClient.contact2_function || "Secundair contact", email: selectedClient.email2 });
    }
    (src?.contacts || []).forEach((ct) => (ct.name || ct.email) && arr.push({ name: ct.name, function: ct.function, email: ct.email }));
    return arr;
  }, [contactSource, selectedClient, selectedProperty]);

  const pickContact = (name) => {
    const ct = contactOptions.find((c) => c.name === name);
    if (ct) { setContactName(ct.name || ""); setContactFunction(ct.function || ""); setContactEmail(ct.email || ""); }
  };

  const recipientValid = recipientType === "debiteur"
    ? !!tenantId
    : (contactSource === "opdrachtgever" ? !!clientId : !!propertyId) && !!contactName.trim();

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const payload = {
        recipient_type: recipientType, tenant_id: tenantId, client_id: clientId, property_id: contactSource === "pand" ? propertyId : "",
        contact_name: contactName, contact_function: contactFunction, set_type: setType, tone, mode, use_general: useGeneral,
        instruction, months_outstanding: months, total_amount: totalAmount ? Number(totalAmount) : null, due_date: dueDate, specification,
        template_client_id: mode === "template" ? (templateClientId === "general" ? "" : templateClientId) : null,
      };
      const r = await api.post("/letter-generator/generate", payload);
      setSubject(r.data.subject); setBodyText(r.data.body); setSpecRows(r.data.spec_rows || []);
      setDefaultTo((r.data.recipients || []).join(", "));
      if (!totalAmount && r.data.auto_amount) { setTotalAmount(String(r.data.auto_amount)); toast.info(`Openstaand bedrag automatisch overgenomen: EUR ${Number(r.data.auto_amount).toFixed(2)}`); }
      if (!months && r.data.auto_months) setMonths(r.data.auto_months);
      if (r.data.complaints_referenced?.length) toast.info(`${r.data.complaints_referenced.length} open klacht(en) verwerkt in de brief: ${r.data.complaints_referenced.join(", ")}`);
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Genereren mislukt"); }
    finally { setGenerating(false); }
  };

  const doSave = async (sendEmail) => {
    setBusy(true);
    try {
      const to = emailTo.split(",").map((x) => x.trim()).filter(Boolean);
      const payload = {
        recipient_type: recipientType, tenant_id: tenantId, client_id: clientId, property_id: contactSource === "pand" ? propertyId : "",
        contact_name: contactName, contact_function: contactFunction, set_type: setType, tone,
        subject, body: bodyText, spec_rows: specRows, send_email: sendEmail, to, dossier_id: dossierId,
      };
      const r = await api.post("/letter-generator/save", payload);
      setSavedLetterId(r.data.letter_id);
      toast.success(sendEmail ? `Brief verzonden naar ${(r.data.recipients || []).join(", ")}` : "Brief opgeslagen en geregistreerd in het log");
      setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setBusy(false); }
  };

  const openPdf = async () => {
    try {
      const r = await api.get(`/letters/${savedLetterId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("PDF openen mislukt"); }
  };

  const StepHeader = () => (
    <div className="flex items-center gap-2 flex-wrap" data-testid="lettergen-steps">
      {["Geadresseerde", "Brief instellen", "Genereren & bewerken", "Afronden"].map((s, i) => (
        <div key={i} className={`flex items-center gap-2 text-sm ${i === step ? "text-indigo-700 font-semibold" : i < step ? "text-emerald-600" : "text-slate-400"}`}>
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${i === step ? "bg-indigo-600 text-white" : i < step ? "bg-emerald-500 text-white" : "bg-slate-200"}`}>{i < step ? "✓" : i + 1}</span>
          {s}{i < 3 && <span className="text-slate-300 mx-1">→</span>}
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto" data-testid="letter-generator-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white"><PenLine className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-[#0F1E3D]">Brievengenerator</h1>
          <p className="text-sm text-slate-500">Stel per stap een brief samen — met AI of vanuit uw sjablonen.</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap" data-testid="lettergen-flow-toggle">
        <button type="button" onClick={() => setFlow("standaard")} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${flow === "standaard" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 hover:border-indigo-300"}`} data-testid="flow-standaard">AI / Sjabloonbrief</button>
        <button type="button" onClick={() => setFlow("incasso")} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${flow === "incasso" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 hover:border-indigo-300"}`} data-testid="flow-incasso">Vaste incassobrief (met Excel-tabel)</button>
      </div>
      {flow === "incasso" ? <IncassoLetterFlow tenants={tenants} clients={clients} initialTenantId={sp.get("incasso") === "1" ? (sp.get("tenant") || "") : ""} initialType={sp.get("type") || ""} initialFrom={sp.get("from") || ""} /> : (<>
      <StepHeader />

      {step === 0 && (
        <Card className="p-6 space-y-5" data-testid="lettergen-step-recipient">
          <div>
            <Label className="mb-2 block">Naar wie gaat de brief?</Label>
            <RadioGroup value={recipientType} onValueChange={setRecipientType} className="grid grid-cols-2 gap-3">
              <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${recipientType === "debiteur" ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/40" : "border-slate-200"}`}>
                <RadioGroupItem value="debiteur" data-testid="rt-debiteur" /> <User className="h-4 w-4" /> <span className="text-sm">Debiteur</span>
              </label>
              <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${recipientType === "contact" ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/40" : "border-slate-200"}`}>
                <RadioGroupItem value="contact" data-testid="rt-contact" /> <Briefcase className="h-4 w-4" /> <span className="text-sm">Contactpersoon (opdrachtgever / pand)</span>
              </label>
            </RadioGroup>
          </div>

          {recipientType === "debiteur" ? (
            <div>
              <Label>Kies debiteur</Label>
              <Combobox testId="lettergen-tenant-select" value={tenantId} onChange={setTenantId} placeholder="Selecteer debiteur"
                options={tenants.map((t) => ({ value: t.id, label: `${t.first_name} ${t.last_name}${t.property_label ? " — " + t.property_label : ""}` }))} />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block">Bron van contactpersoon</Label>
                <RadioGroup value={contactSource} onValueChange={setContactSource} className="grid grid-cols-2 gap-3">
                  <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${contactSource === "opdrachtgever" ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/40" : "border-slate-200"}`}>
                    <RadioGroupItem value="opdrachtgever" data-testid="cs-client" /> <Briefcase className="h-4 w-4" /> <span className="text-sm">Opdrachtgever</span>
                  </label>
                  <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${contactSource === "pand" ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/40" : "border-slate-200"}`}>
                    <RadioGroupItem value="pand" data-testid="cs-property" /> <Building2 className="h-4 w-4" /> <span className="text-sm">Pand</span>
                  </label>
                </RadioGroup>
              </div>
              {contactSource === "opdrachtgever" ? (
                <div>
                  <Label>Opdrachtgever</Label>
                  <Combobox testId="lettergen-client-select" value={clientId} onChange={(v) => { setClientId(v); setContactName(""); setContactEmail(""); }} placeholder="Selecteer opdrachtgever"
                    options={clients.map((c) => ({ value: c.id, label: c.name }))} />
                </div>
              ) : (
                <div>
                  <Label>Pand</Label>
                  <Combobox testId="lettergen-property-select" value={propertyId} onChange={(v) => { setPropertyId(v); setContactName(""); setContactEmail(""); }} placeholder="Selecteer pand" searchPlaceholder="Zoek pand…"
                    options={properties.map((p) => ({ value: p.id, label: `${p.street} ${p.number}, ${p.city}` }))} />
                </div>
              )}
              {(contactSource === "pand" ? !!propertyId : !!clientId) && (
                <div>
                  <Label>Contactpersonen {contactSource === "pand" ? "van dit pand" : "van deze opdrachtgever"}</Label>
                  {contactOptions.length === 0 ? (
                    <p className="text-xs text-slate-400 mt-1" data-testid="lettergen-no-contacts">Geen contactpersonen gekoppeld aan {contactSource === "pand" ? "dit pand" : "deze opdrachtgever"}. Vul hieronder handmatig in.</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-2 mt-1" data-testid="lettergen-contact-list">
                      {contactOptions.map((c, i) => {
                        const active = c.name === contactName;
                        return (
                          <button type="button" key={i} onClick={() => pickContact(c.name)} data-testid={`lettergen-contact-option-${i}`}
                            className={`text-left border rounded-lg p-3 transition-colors ${active ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/50" : "border-slate-200 hover:border-indigo-300"}`}>
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-800 truncate">{c.name || "Onbekend"}</div>
                                <div className="text-xs text-indigo-600 truncate">{c.function || "Functie onbekend"}</div>
                                {c.email && <div className="text-[11px] text-slate-400 truncate">{c.email}</div>}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Naam *</Label><Input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="lettergen-contact-name" /></div>
                <div><Label className="text-xs">Functie</Label><Input value={contactFunction} onChange={(e) => setContactFunction(e.target.value)} data-testid="lettergen-contact-function" /></div>
                <div><Label className="text-xs">E-mail</Label><Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="lettergen-contact-email" /></div>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" disabled={!recipientValid} onClick={() => setStep(1)} data-testid="lettergen-next-1">Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card className="p-6 space-y-5" data-testid="lettergen-step-config">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Set</Label>
              <Combobox testId="lettergen-set-select" value={setType} onChange={setSetType} options={SETS.map((s) => ({ value: s.v, label: s.l }))} />
            </div>
            <div>
              <Label>Tonatie</Label>
              <Combobox testId="lettergen-tone-select" value={tone} onChange={setTone} options={TONES.map((t) => ({ value: t, label: t }))} />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Hoe wilt u de brief opstellen?</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-2 gap-3">
              <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${mode === "ai" ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/40" : "border-slate-200"}`}>
                <RadioGroupItem value="ai" data-testid="mode-ai" /> <Sparkles className="h-4 w-4" /> <span className="text-sm">AI-generatie (Claude)</span>
              </label>
              <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${mode === "template" ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/40" : "border-slate-200"}`}>
                <RadioGroupItem value="template" data-testid="mode-template" /> <FileText className="h-4 w-4" /> <span className="text-sm">Uit sjabloonset</span>
              </label>
            </RadioGroup>
            {mode === "ai" && (
              <label className="flex items-center gap-2 mt-3 text-sm text-slate-600">
                <input type="checkbox" checked={useGeneral} onChange={(e) => setUseGeneral(e.target.checked)} data-testid="lettergen-use-general" />
                Gebruik de <strong>standaard</strong> set als basis (negeer opdrachtgever-specifieke sjablonen)
              </label>
            )}
            {mode === "template" && (
              <div className="mt-3">
                <Label className="text-xs">Sjabloonset van opdrachtgever</Label>
                <Combobox testId="lettergen-template-client" value={templateClientId} onChange={setTemplateClientId} searchPlaceholder="Zoek opdrachtgever…"
                  options={[{ value: "general", label: "Algemeen (standaard)" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} />
                <p className="text-[11px] text-slate-400 mt-1">Kies van welke opdrachtgever de sjabloonset gebruikt wordt (of Algemeen).</p>
              </div>
            )}
          </div>

          {recipientType === "debiteur" && (
            <div className="border rounded-xl p-4 space-y-3 bg-slate-50/60">
              <div className="text-xs font-semibold text-slate-500">Gegevens debiteur</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Aantal maanden / periode</Label><Input value={months} onChange={(e) => setMonths(e.target.value)} placeholder="leeg = automatisch uit openstaande termijnen" data-testid="lettergen-months" /></div>
                <div><Label className="text-xs">Totaalbedrag (EUR)</Label><Input type="number" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} placeholder="leeg = automatisch uit contract/termijnen" data-testid="lettergen-total" /></div>
                <div><Label className="text-xs">Uiterste betaaldatum</Label><DateField testId="lettergen-due" value={dueDate} onChange={setDueDate} /></div>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Table2 className="h-3.5 w-3.5" /> Specificatie facturen (plak vanuit Excel)</Label>
                <Textarea rows={5} value={specification} onChange={(e) => setSpecification(e.target.value)} placeholder={"Factuurnr.\tDatum\tOmschrijving\tBedrag\n2024-001\t01-03-2024\tHuur maart\t€ 850,00"} className="font-mono text-xs" data-testid="lettergen-specification" />
                <p className="text-[11px] text-slate-400 mt-1">Kolommen (tab-gescheiden): Factuurnr., Datum, Omschrijving, Bedrag. Wordt als tabel onder de brief geplaatst.</p>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">{recipientType === "contact" || setType === "algemeen" ? "Wat moet er in de brief staan? (input voor AI / algemene brief)" : "Extra toelichting (optioneel)"}</Label>
            <Textarea rows={4} value={instruction} onChange={(e) => setInstruction(e.target.value)} data-testid="lettergen-instruction" placeholder="bijv. Meld dat de betalingsregeling is komen te vervallen wegens het niet nakomen van afspraken." />
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={doGenerate} disabled={generating} data-testid="lettergen-generate-btn">
              {generating ? "Bezig…" : <><Sparkles className="h-4 w-4 mr-2" /> Genereren</>}
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 space-y-4" data-testid="lettergen-step-preview">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-slate-800">Concept — controleer en bewerk</h2>
            <Badge className="bg-indigo-100 text-indigo-700">{SETS.find((s) => s.v === setType)?.l} · {tone}</Badge>
          </div>
          <div><Label>Onderwerp</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="lettergen-edit-subject" /></div>
          <div><Label>Brieftekst</Label><Textarea rows={14} value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid="lettergen-edit-body" /></div>
          {specRows.length > 0 && (
            <div>
              <Label className="text-xs flex items-center gap-1"><Table2 className="h-3.5 w-3.5" /> Specificatie (wordt als tabel toegevoegd)</Label>
              <div className="border rounded-lg overflow-x-auto mt-1" data-testid="lettergen-spec-preview">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-800 text-white text-left"><th className="p-2">Factuurnr.</th><th className="p-2">Datum</th><th className="p-2">Omschrijving</th><th className="p-2">Bedrag</th></tr></thead>
                  <tbody>{specRows.map((r, i) => <tr key={i} className="border-b"><td className="p-2">{r[0]}</td><td className="p-2">{r[1]}</td><td className="p-2">{r[2]}</td><td className="p-2">{r[3]}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => { setEmailTo(contactEmail || defaultTo || ""); setStep(3); }} disabled={!subject.trim() || !bodyText.trim()} data-testid="lettergen-to-finish">Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card className="p-6 space-y-5" data-testid="lettergen-step-finish">
          {!savedLetterId ? (
            <>
              <h2 className="font-heading font-semibold text-slate-800">Afronden</h2>
              {recipientType === "debiteur" && dossiers.length > 0 && (
                <div>
                  <Label>Toevoegen aan dossier (optioneel)</Label>
                  <Combobox testId="lettergen-dossier-select" value={dossierId || "none"} onChange={(v) => setDossierId(v === "none" ? "" : v)} searchPlaceholder="Zoek dossier…"
                    options={[{ value: "none", label: "Niet toevoegen" }, ...dossiers.map((d) => ({ value: d.id, label: `${d.number} — ${d.title}` }))]} />
                </div>
              )}
              <div>
                <Label>E-mailadres(sen) voor verzending (komma-gescheiden)</Label>
                <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="naam@voorbeeld.nl" data-testid="lettergen-email-to" />
                <p className="text-[11px] text-slate-400 mt-1">Alle gegenereerde brieven worden geregistreerd in het log{recipientType === "debiteur" ? " en (indien gekozen) aan het dossier toegevoegd" : " van de opdrachtgever"}.</p>
              </div>
              <div className="flex flex-wrap justify-between gap-3">
                <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => doSave(false)} disabled={busy} data-testid="lettergen-save-btn"><FileText className="h-4 w-4 mr-2" /> Alleen opslaan</Button>
                  <Button className="bg-[#C9A227] hover:bg-[#B8921F] text-[#0F1E3D]" onClick={() => doSave(true)} disabled={busy || !emailTo.trim()} data-testid="lettergen-send-btn"><Send className="h-4 w-4 mr-2" /> Opslaan & versturen</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-6 space-y-4" data-testid="lettergen-done">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
              <h2 className="font-heading font-semibold text-slate-800 text-lg">Brief opgeslagen en geregistreerd</h2>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={openPdf} data-testid="lettergen-open-pdf"><Download className="h-4 w-4 mr-2" /> PDF openen</Button>
                <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => { setSavedLetterId(""); setStep(0); setSubject(""); setBodyText(""); setSpecRows([]); setSpecification(""); setInstruction(""); }} data-testid="lettergen-new"><PenLine className="h-4 w-4 mr-2" /> Nieuwe brief</Button>
              </div>
            </div>
          )}
        </Card>
      )}
      </>)}
    </div>
  );
}
